import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

// Import the service modules
import { circleService } from '../../services/circleService';
import { authService } from '../../services/authService';

// Create the context
const CircleDataContext = createContext(null);

/**
 * Provider component that fetches and manages circle data
 * Enhanced to integrate data from backend endpoints with proper filtering
 * Now with improved refresh mechanism for status updates
 */
export const CircleDataProvider = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated, user: auth0User } = useAuth0();

  // User circles state
  const [userCircles, setUserCircles] = useState([]);
  const [selectedCircleId, setSelectedCircleId] = useState(null);
  const [circleData, setCircleData] = useState(null);
  const [tierData, setTierData] = useState(null);
  const [tiers, setTiers] = useState([]);

  // Loading and error states
  const [loadingCircles, setLoadingCircles] = useState(true);
  const [loadingCircleDetails, setLoadingCircleDetails] = useState(false);
  const [error, setError] = useState(null);

  // Last updated timestamp for potential refetching
  const [lastUpdated, setLastUpdated] = useState(Date.now());

  // Enhanced cache for circle details to store data for multiple circles
  const [circleCache, setCircleCache] = useState({});

  // New state to track if we're currently fetching data for a circle
  const [fetchingCircleId, setFetchingCircleId] = useState(null);

  // New state to store aggregated price data for all circles
  const [circlePriceMap, setCirclePriceMap] = useState({});

  // Helper function to determine shipping method with better logic
  const determineShippingMethod = (circle) => {
    console.log('Determining shipping method for circle:', circle.name, {
      rawShippingMethod: circle.shipping_method,
      altShippingMethod: circle.shippingMethod,
      subscriptionStatus: circle.subscription_status || circle.statusDisplay,
      hasShippingMethod: 'shipping_method' in circle,
      hasShippingMethodAlt: 'shippingMethod' in circle,
      circleData: circle
    });

    // Check for shipping_method with different property names
    if (circle.shipping_method) return circle.shipping_method;
    if (circle.shippingMethod) return circle.shippingMethod;

    // Look at the subscription status and determine shipping method
    const status = (circle.subscription_status || circle.statusDisplay || '').toLowerCase();

    // For 'activate' status with no explicit shipping_method, default to eSIM
    if (status.includes('activate') || status.includes('esim_initiated')) {
      console.log(`Setting eSIM for activate status circle: ${circle.name}`);
      return 'eSIM';
    }

    // For joined subscriptions (which often miss shipping_method), check the admin
    if (circle.source === 'joined_subscription' || circle.source === 'subscription') {
      // Try to determine from other properties...
      if (status.includes('esim')) return 'eSIM';
      if (status.includes('activate')) return 'eSIM';
    }

    // Default fallback - retain previous behavior but log it
    console.log(`Defaulting to 'standard' shipping for circle: ${circle.name}`);
    return 'standard';
  };

  // Fetch all user circles - now using backend filtered endpoints
  // In CircleDataContext.js - Update the useEffect that fetches user circles
  useEffect(() => {
    if (!isAuthenticated || !auth0User) return;

    const fetchUserCircles = async () => {
      try {
        setLoadingCircles(true);
        setError(null);

        // Get Auth0 token for authenticated API request
        let token;
        try {
          // Ensure we're using the correct audience from the environment variables
          token = await getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: 'openid profile email'
          });
          console.log("Successfully obtained Auth0 token for API requests");
        } catch (tokenError) {
          console.error('Error getting Auth0 token:', tokenError);
          setError('Authentication error: Unable to get access token. Please try logging out and back in.');
          setLoadingCircles(false);
          return; // Exit early if we can't get a token
        }

        // Store all circles from all sources
        let allCircles = [];
        let ownedCirclesList = [];
        let joinedCirclesList = [];

        // APPROACH 1: Use the consolidated endpoint for all circles (both owned and joined)
        try {
          console.log("Fetching all user circles from consolidated endpoint...");
          const allCirclesResponse = await circleService.getAllUserCircles(token);
          console.log("All user circles API response:", allCirclesResponse.data);

          if (allCirclesResponse.data && Array.isArray(allCirclesResponse.data)) {
            // Process the circles to ensure consistent format
            const processedCircles = allCirclesResponse.data.map(circle => {
              // Get display status
              const statusDisplay = getDisplayStatus(circle);

              // Return properly formatted circle object
              return {
                id: circle.id,
                name: circle.name || 'Circle',
                plan: circle.plan_name || 'Unlimited Plan',
                plan_id: circle.plan_id,
                carrier: circle.carrier,
                status: circle.status || 'inactive',
                subscription_status: circle.subscription_status,
                statusDisplay: statusDisplay,
                members: circle.members || [],
                ageInMonths: getAgeInMonths(circle.created_at),
                isOwner: circle.is_owner === true, // Use backend flag
                monthlyPayment: parseFloat(circle.monthly_payment_per_line || circle.amount) || 0,
                stripe_subscription_id: circle.stripe_subscription_id,
                paymentStatus: circle.status === 'active' ? 'On time' : 'Late',
                hasIssues: circle.status === 'payment_failed' || circle.status === 'past_due' || circle.status === 'canceling',
                issues: getIssuesFromCircle(circle),
                available_slots: circle.available_slots || 0,
                total_joiners: circle.total_joiners || 0,
                family_size: circle.family_size || 2,
                maxCapacity: circle.max_capacity || 10,
                features: circle.features || extractFeaturesFromPlan(circle.plan_name),
                monthly_payment_date: circle.monthly_payment_date,
                owner_id: circle.owner_id,
                source: circle.is_owner ? 'circle-owner' : 'subscription'
              };
            });

            // Add all circles to our array
            allCircles = [...processedCircles];

            // Split into owned and joined based on the is_owner flag
            // This flag is set by the backend, so we can trust it
            ownedCirclesList = processedCircles.filter(circle => circle.isOwner === true);
            joinedCirclesList = processedCircles.filter(circle => circle.isOwner !== true);

            console.log('Backend returned - All Circles:', allCircles.length);
            console.log('Backend returned - Owned Circles:', ownedCirclesList.length);
            console.log('Backend returned - Joined Circles:', joinedCirclesList.length);
          } else {
            // Handle unexpected response format
            console.error("Unexpected response format from all-circles endpoint:", allCirclesResponse);
            throw new Error("Unexpected response format from API");
          }
        } catch (allCirclesError) {
          console.error("Error fetching all user circles:", allCirclesError);

          // APPROACH 2: Fallback to fetching owned and joined circles separately
          console.log("Fallback: Fetching owned and joined circles separately...");

          // Get owned circles
          try {
            console.log("Fetching circles owned by user...");
            const ownedCirclesResponse = await circleService.getUserOwnedCircles(token);
            console.log("Owned circles API response:", ownedCirclesResponse.data);

            if (ownedCirclesResponse.data && Array.isArray(ownedCirclesResponse.data)) {
              // Process owned circles
              const processedOwned = ownedCirclesResponse.data.map(circle => ({
                id: circle.id,
                name: circle.name || 'Circle',
                plan: circle.plan_name || 'Unlimited Plan',
                plan_id: circle.plan_id,
                carrier: circle.carrier,
                status: circle.status || 'inactive',
                subscription_status: circle.subscription_status,
                statusDisplay: getDisplayStatus(circle),
                members: circle.members || [],
                ageInMonths: getAgeInMonths(circle.created_at),
                isOwner: true, // These are definitely owned
                monthlyPayment: parseFloat(circle.monthly_payment_per_line) || 0,
                paymentStatus: 'On time',
                hasIssues: false,
                issues: [],
                available_slots: circle.available_slots || 0,
                total_joiners: circle.total_joiners || 0,
                family_size: circle.family_size || 2,
                maxCapacity: circle.max_capacity || 10,
                features: circle.features || extractFeaturesFromPlan(circle.plan_name),
                monthly_payment_date: circle.monthly_payment_date,
                owner_id: circle.owner_id,
                source: 'circle-owner'
              }));

              ownedCirclesList = processedOwned;
              allCircles = [...allCircles, ...processedOwned];
              console.log(`Found ${ownedCirclesList.length} owned circles`);
            } else {
              console.warn("Unexpected response format from owned-circles endpoint:", ownedCirclesResponse);
            }
          } catch (ownedError) {
            console.error("Error fetching owned circles:", ownedError);
          }

          // Get joined circles
          try {
            console.log("Fetching circles joined by user...");
            const joinedCirclesResponse = await circleService.getUserJoinedCircles(token);
            console.log("Joined circles API response:", joinedCirclesResponse.data);

            if (joinedCirclesResponse.data && Array.isArray(joinedCirclesResponse.data)) {
              // Process joined circles
              const processedJoined = joinedCirclesResponse.data.map(circle => ({
                id: circle.id,
                name: circle.name || 'Circle',
                plan: circle.plan_name || 'Unlimited Plan',
                plan_id: circle.plan_id,
                carrier: circle.carrier,
                status: circle.status || 'inactive',
                subscription_status: 'joined', // These are explicitly joined
                statusDisplay: getDisplayStatus({ ...circle, subscription_status: 'joined' }),
                members: circle.members || [],
                ageInMonths: getAgeInMonths(circle.created_at),
                isOwner: false, // These are definitely not owned
                monthlyPayment: parseFloat(circle.amount || circle.monthly_payment_per_line) || 0,
                paymentStatus: circle.status === 'active' ? 'On time' : 'Late',
                hasIssues: circle.status === 'payment_failed' || circle.status === 'past_due' || circle.status === 'canceling',
                issues: getIssuesFromCircle(circle),
                available_slots: circle.available_slots || 0,
                total_joiners: circle.total_joiners || 0,
                family_size: circle.family_size || 2,
                maxCapacity: circle.max_capacity || 10,
                features: circle.features || extractFeaturesFromPlan(circle.plan_name),
                monthly_payment_date: circle.monthly_payment_date,
                owner_id: circle.owner_id,
                source: 'joined_subscription'
              }));

              joinedCirclesList = processedJoined;
              allCircles = [...allCircles, ...processedJoined];
              console.log(`Found ${joinedCirclesList.length} joined circles`);
            } else {
              console.warn("Unexpected response format from joined-circles endpoint:", joinedCirclesResponse);
            }
          } catch (joinedError) {
            console.error("Error fetching joined circles:", joinedError);
          }
        }

        // If we still have no circles, log additional diagnostics
        if (allCircles.length === 0) {
          console.warn("No circles found through any method. User info:", {
            auth0Id: auth0User.sub,
            email: auth0User.email,
            isAuthenticated
          });

          // As a last resort, try fetching using a different audience 
          try {
            console.log("Attempting fallback with different audience...");
            const fallbackToken = await getAccessTokenSilently({
              audience: 'https://circledin.us.auth0.com/api/v2/',
              scope: 'openid profile email'
            });

            const fallbackResponse = await circleService.getAllUserCircles(fallbackToken);
            console.log("Fallback response:", fallbackResponse.data);

            if (fallbackResponse.data && Array.isArray(fallbackResponse.data) && fallbackResponse.data.length > 0) {
              // Process and use this data if found
              allCircles = [...fallbackResponse.data];
              console.log(`Fallback found ${allCircles.length} circles`);

              // Update lists
              ownedCirclesList = allCircles.filter(circle => circle.is_owner === true);
              joinedCirclesList = allCircles.filter(circle => circle.is_owner !== true);
            }
          } catch (fallbackError) {
            console.error("Fallback attempt failed:", fallbackError);
          }
        }

        // Enrich circles with necessary properties for port-in functionality
        const enrichedCircles = allCircles.map(circle => {
          // Check if the circle has a subscription status with "shipment"
          const hasShipmentStatus =
            (circle.subscription_status && circle.subscription_status.toLowerCase().includes('ship')) ||
            (circle.statusDisplay && circle.statusDisplay.toLowerCase().includes('ship'));

          // Return properly formatted and enriched circle object
          return {
            ...circle,
            // Ensure subscription_status exists
            subscription_status: circle.subscription_status || circle.statusDisplay || (hasShipmentStatus ? 'shipment' : 'unknown'),

            // Use the helper function to determine shipping method correctly
            shipping_method: determineShippingMethod(circle),

            // Ensure joining_condition exists (make eligible for port)
            joining_condition: circle.joining_condition || circle.joiningCondition || 'switching_carriers',

            // Add debug flag to identify enriched circles
            _enriched: true
          };
        });

        // Update state with all collected circles
        setUserCircles(enrichedCircles);

        // If we don't have a selected circle yet, choose the first one
        if (!selectedCircleId && enrichedCircles.length > 0) {
          // Look for active circle first
          const activeCircle = enrichedCircles.find(c =>
            c.statusDisplay === 'Active' || c.status === 'active');
          setSelectedCircleId(activeCircle ? activeCircle.id : enrichedCircles[0].id);
        }

        console.log(`Final counts: All=${enrichedCircles.length}, Owned=${ownedCirclesList.length}, Joined=${joinedCirclesList.length}`);
      } catch (err) {
        console.error('Error fetching user circles:', err);
        setError(err.message || 'Failed to load your circles. Please try again later.');
      } finally {
        setLoadingCircles(false);
      }
    };

    fetchUserCircles();
  }, [getAccessTokenSilently, isAuthenticated, lastUpdated, selectedCircleId, auth0User?.sub]);

  // Utility function to get issues from a circle
  const getIssuesFromCircle = (circle) => {
    const issues = [];

    if (circle.status === 'payment_failed' || circle.status === 'past_due') {
      issues.push('Late payment');
    }

    if (circle.subscription_status === 'pending_approval') {
      issues.push('Awaiting approval');
    }

    if (circle.subscription_status?.includes('transfer')) {
      issues.push('Transfer in progress');
    }

    if (circle.status === 'canceling') {
      if (circle.monthly_payment_date) {
        const date = new Date(circle.monthly_payment_date);
        const formattedDate = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
        issues.push(`Canceling on ${formattedDate}`);
      } else {
        issues.push('Canceling at end of billing period');
      }
    }

    return issues;
  };

  // Utility function - Get the display status
  const getDisplayStatus = (circle) => {
    // Check for port_in_progress first - highest priority status display
    if (circle.subscription_status === 'port_in_progress') {
      return 'Port in Progress';
    }

    // For circle owner statuses (from the CircleOwner model)
    if (circle.source === 'circle-owner') {
      // Map Django model statuses to frontend display values with proper capitalization
      // Exact match for Django model's PLAN_STATUS_CHOICES values
      if (circle.status === 'pending_approval') return 'Pending Approval';
      // Use 'Active' for both approved/approve to maintain UI consistency
      if (circle.status === 'approved' || circle.status === 'Approved' || circle.status === 'Approve' ||circle.status === 'approve') return 'Active';
      if (circle.status === 'declined' || circle.status === 'decline') return 'Declined';
      if (circle.status === 'joined') return 'Active'; // Show joined circles as Active too

      // Additional check for subscription_status which may override circle status
      if (circle.subscription_status === 'shipment') return 'Shipment';
      if (circle.subscription_status === 'sim_received') return 'Shipment';
      if (circle.subscription_status === 'esim_initiated') return 'ESIM Initiated';
      if (circle.subscription_status === 'port_in_progress') return 'Port in Progress';
      if (circle.subscription_status?.includes('transfer')) return 'Transfer';

      // For unknown statuses, default to a safe value that will work with the UI
      return 'Active';
    }

    // For subscription statuses (from the Order model)
    if (circle.source === 'subscription' || circle.source === 'joined_subscription' || !circle.source) {
      // First check subscription_status for CircledIn status
      if (circle.subscription_status === 'pending_approval') return 'Pending Approval';

      // FIXED: Display ESIM Initiated correctly, separate from Shipment status
      if (circle.subscription_status === 'esim_initiated') return 'ESIM Initiated';

      // Keep these statuses as Shipment
      if (circle.subscription_status === 'shipment' ||
        circle.subscription_status === 'sim_received') return 'Shipment';

      if (circle.subscription_status === 'activate' ||
        circle.subscription_status === 'joined') return 'Active';
      if (circle.subscription_status?.includes('transfer')) return 'Transfer';
      if (circle.subscription_status === 'decline') return 'Declined';
      if (circle.subscription_status === 'deleted') return 'Deleted';
      if (circle.subscription_status === 'port_in_progress') return 'Port in Progress';

      // Then fall back to Stripe status
      if (circle.status === 'canceling') return 'Canceling';
      if (circle.status === 'active' || circle.status === 'trialing') return 'Active';
      if (circle.status === 'paid') return 'Active';

      // Display actual status from backend when possible, with proper capitalization
      if (circle.status) {
        // Capitalize first letter and replace underscores with spaces
        return circle.status.charAt(0).toUpperCase() + circle.status.slice(1).replace(/_/g, ' ');
      }

      // Final fallback
      return 'Active';
    }

    // For unknown sources, try to use the actual status if provided
    if (circle.status) {
      return circle.status.charAt(0).toUpperCase() + circle.status.slice(1).replace(/_/g, ' ');
    }

    // Final fallback
    return 'Active';
  };

  // Feature extraction - utility from original implementation
  const extractFeaturesFromPlan = (planName) => {
    if (!planName) return ['Unlimited', 'Security'];

    const features = [];
    const planLower = planName.toLowerCase();

    if (planLower.includes('unlimited')) features.push('Unlimited');
    if (planLower.includes('5g')) features.push(planLower.includes('ultra') ? '5G Ultra' : '5G');
    if (planLower.includes('disney')) features.push('Disney+');
    if (planLower.includes('netflix')) features.push('Netflix');
    if (planLower.includes('hbo')) features.push('HBO Max');
    if (planLower.includes('max')) features.push(features.includes('HBO Max') ? null : 'Max');
    if (planLower.includes('security') || planLower.includes('protection')) features.push('Security');
    if (planLower.includes('plus')) features.push(features.length === 0 ? 'Plus' : null);

    if (features.filter(Boolean).length < 2) {
      if (!features.includes('Unlimited')) features.push('Unlimited');
      if (features.filter(Boolean).length < 2) features.push('Security');
    }

    return features.filter(Boolean);
  };

  // Get carrier from plan name - utility from original implementation
  const getCarrierFromPlan = (planName) => {
    if (!planName) return 'unknown';

    const planLower = planName.toLowerCase();
    if (planLower.includes('verizon')) return 'verizon';
    if (planLower.includes('at&t') || planLower.includes('att')) return 'att';
    if (planLower.includes('t-mobile') || planLower.includes('tmobile')) return 'tmobile';
    if (planLower.includes('cricket')) return 'cricket';

    return 'unknown';
  };

  // Get the age of a circle in months - utility from original implementation
  const getAgeInMonths = (createdAt) => {
    if (!createdAt) return 0;

    const created = new Date(createdAt);
    const now = new Date();

    const monthDiff = (now.getFullYear() - created.getFullYear()) * 12 + (now.getMonth() - created.getMonth());
    return Math.max(1, monthDiff); // At least 1 month
  };

  // Update circlePriceMap whenever circleCache or userCircles change
  useEffect(() => {
    // This effect aggregates all tier pricing data for every circle we have in the cache
    const updateCirclePriceMap = () => {
      const newPriceMap = {};

      // Create entries for all circles first with null values
      userCircles.forEach(circle => {
        if (!newPriceMap[circle.id]) {
          newPriceMap[circle.id] = {
            currentPrice: circle.monthlyPayment || 0,
            nextTierPrice: null,
            savingsPerLine: 0,
            isMaxTier: false,
            hasLoaded: false
          };
        }
      });

      // Then update with cached data where available
      Object.keys(circleCache).forEach(circleId => {
        const cachedData = circleCache[circleId];
        if (cachedData && cachedData.tierData) {
          const currentPrice = cachedData.tierData.currentTier?.price_per_line || 0;
          const nextTierPrice = cachedData.tierData.nextTier?.price_per_line;
          const isMaxTier = !cachedData.tierData.nextTier || cachedData.tierData.membersToNextTier <= 0;
          const savingsPerLine = nextTierPrice ? (currentPrice - nextTierPrice) : 0;

          newPriceMap[circleId] = {
            currentPrice,
            nextTierPrice: isMaxTier ? currentPrice : nextTierPrice,
            savingsPerLine,
            isMaxTier,
            membersToNextTier: cachedData.tierData.membersToNextTier || 0,
            hasLoaded: true
          };
        }
      });

      setCirclePriceMap(newPriceMap);
    };

    updateCirclePriceMap();
  }, [circleCache, userCircles]);

  // Fetch circle details when selectedCircleId changes
  useEffect(() => {
    if (!selectedCircleId) return;

    const fetchSelectedCircleDetails = async () => {
      await fetchCircleDetails(selectedCircleId);
    };

    fetchSelectedCircleDetails();
  }, [selectedCircleId]);

  // Prefetch tier data for all circles to populate the price map
  useEffect(() => {
    const prefetchAllCircles = async () => {
      // Skip if we don't have user circles loaded yet
      if (userCircles.length === 0) return;

      // Track which circles we've processed to avoid duplicates
      const processedCircles = new Set();

      // First handle the selected circle if any
      if (selectedCircleId) {
        await fetchCircleDetails(selectedCircleId);
        processedCircles.add(selectedCircleId);
      }

      // Then prefetch data for all other circles
      for (const circle of userCircles) {
        if (!processedCircles.has(circle.id) && !circleCache[circle.id]) {
          // Stagger requests to avoid overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 300));
          await fetchCircleDetails(circle.id, true);
          processedCircles.add(circle.id);
        }
      }
    };

    // Run the prefetch
    prefetchAllCircles();
  }, [userCircles]);

  // Main function to fetch circle details - supports both direct fetching and prefetching
  const fetchCircleDetails = async (circleId, isPrefetch = false) => {
    // Check if we have fresh cached data for this circle ID (less than 5 minutes old)
    const cachedData = circleCache[circleId];
    const isCacheFresh = cachedData &&
      (Date.now() - cachedData.timestamp < 5 * 60 * 1000);

    if (isCacheFresh && !isPrefetch) {
      // Use cached data for the selected circle
      setCircleData(cachedData.circleData);
      setTierData(cachedData.tierData);
      setTiers(cachedData.tiers);
      return;
    }

    // Check if we're already fetching this circle to prevent duplicate requests
    if (fetchingCircleId === circleId) {
      return;
    }

    try {
      if (!isPrefetch) {
        setLoadingCircleDetails(true);
      }
      setFetchingCircleId(circleId);
      setError(null);

      // Get Auth0 token for authenticated API request
      let token;
      try {
        token = await getAccessTokenSilently({
          audience: 'https://circledin.us.auth0.com/api/v2/',
          scope: 'openid profile email'
        });
      } catch (tokenError) {
        console.error('Error getting Auth0 token:', tokenError);
      }

      // Find the selected circle to determine source
      const selectedCircle = userCircles.find(c => c.id === circleId);

      // Variable to store the response data
      let data;

      // Try using the circle service based on whether it's a joined or owned circle
      try {
        console.log(`Fetching circle plan details for circle ${circleId}`);

        // Use different endpoints based on circle type
        if (selectedCircle && !selectedCircle.isOwner) {
          // For joined circles, try subscription details first
          try {
            const response = await circleService.getSubscriptionDetails(circleId, token);
            data = response.data;
            console.log(`API Response from circleService.getSubscriptionDetails for ${circleId}:`, data);
          } catch (subError) {
            // Fall back to circle plan details
            const response = await circleService.getCirclePlanDetails(circleId, token);
            data = response.data;
            console.log(`Fallback to CirclePlanDetails for joined circle ${circleId}:`, data);
          }
        } else {
          // For owned circles or unknown, try circle plan details
          const response = await circleService.getCirclePlanDetails(circleId, token);
          data = response.data;
          console.log(`API Response from circleService.getCirclePlanDetails for ${circleId}:`, data);
        }
      } catch (primaryError) {
        console.log(`Primary endpoint failed for ${circleId} with error:`, primaryError);

        // If the primary endpoint fails, try fallback endpoint
        try {
          // Try circle details as fallback for owned circles
          if (selectedCircle && selectedCircle.isOwner) {
            console.log(`Trying fallback with circleService.getCircleDetails for ${circleId}`);
            const response = await circleService.getCircleDetails(circleId, token);
            data = response.data;
          } else {
            // For joined circles, try circle plan details as fallback
            console.log(`Trying fallback with circleService.getCirclePlanDetails for ${circleId}`);
            const response = await circleService.getCirclePlanDetails(circleId, token);
            data = response.data;
          }
          console.log(`API Response from fallback endpoint for ${circleId}:`, data);
        } catch (fallbackError) {
          console.log(`Fallback endpoint failed for ${circleId}:`, fallbackError);

          // If all endpoints failed, throw error
          throw new Error(`All endpoints failed to return data for circle ${circleId}`);
        }
      }

      // If we have valid data, process it
      if (data) {
        // Store the complete response data - only update current circle data if not prefetch
        if (!isPrefetch && circleId === selectedCircleId) {
          setCircleData(data);
        }

        // Extract circle and plan data for tier calculations
        const circle = data.circle || {};
        const selectedPlan = data.selected_plan || {};
        const carrier = data.carrier?.name?.toLowerCase() || '';

        if (selectedPlan?.pricing?.pricing_tiers) {
          let pricingTiers = selectedPlan.pricing.pricing_tiers || [];

          // Sort the pricing tiers by line_count to ensure correct order
          pricingTiers.sort((a, b) => a.line_count - b.line_count);

          // Determine the maximum number of tiers to show based on carrier
          let maxTiers = 10; // Default for Verizon, T-Mobile
          if (carrier.includes('att') || carrier.includes('at&t')) {
            maxTiers = 20; // AT&T supports up to 20 lines
          }

          // Get the lowest price from the pricing tiers (usually the highest line count)
          const highestTier = [...pricingTiers].sort((a, b) => b.line_count - a.line_count)[0];
          const lowestPrice = highestTier?.price_per_line || 0;
          const highestSavings = highestTier?.savings_percentage || 0;

          // Extend the pricing tiers up to the carrier's max if needed
          if (pricingTiers.length < maxTiers) {
            const lastDefinedTier = pricingTiers[pricingTiers.length - 1];
            const lastLineCount = lastDefinedTier?.line_count || 1;

            for (let i = lastLineCount + 1; i <= maxTiers; i++) {
              pricingTiers.push({
                line_count: i,
                price_per_line: lowestPrice,
                savings_percentage: highestSavings,
                savings_amount: lastDefinedTier?.savings_amount || 0
              });
            }
          }

          // Find current tier based on total_joiners
          const totalJoiners = circle.total_joiners || 0;
          const familySize = circle.family_size || 0;
          const totalExistingMembers = totalJoiners + familySize;

          // Find the tier that matches the total existing members
          let currentTierIndex = pricingTiers.findIndex(tier => tier.line_count === totalExistingMembers);

          // If no exact match, find the closest tier that can accommodate the existing members
          if (currentTierIndex === -1) {
            const tiers = pricingTiers.filter(tier => tier.line_count >= totalExistingMembers);
            if (tiers.length > 0) {
              // Get the first tier that can fit the joiners
              currentTierIndex = pricingTiers.findIndex(tier => tier.line_count === tiers[0].line_count);
            } else {
              // If all tiers are smaller than total_joiners, use the highest tier
              currentTierIndex = pricingTiers.length - 1;
            }
          }

          const currentTier = currentTierIndex >= 0
            ? pricingTiers[currentTierIndex]
            : pricingTiers[0]; // Default to first tier if not found

          // Find next tier (the next higher line count)
          const nextTierIndex = pricingTiers.findIndex(
            tier => tier.line_count > totalExistingMembers
          );

          const nextTier = nextTierIndex >= 0
            ? pricingTiers[nextTierIndex]
            : null; // No next tier if at max

          // Calculate members needed for next tier
          const membersToNextTier = nextTier
            ? nextTier.line_count - totalExistingMembers
            : 0;

          // Create formatted tiers array for display
          const formattedTiers = pricingTiers.map(tier => ({
            members: tier.line_count,
            label: tier.line_count === 1 ? "Solo Plan" : `${tier.line_count} Members`,
            price: tier.price_per_line,
            savings: tier.savings_percentage,
            isCurrent: tier.line_count === totalExistingMembers,
            isNext: nextTier ? tier.line_count === nextTier.line_count : false
          }));

          // Create tierData object
          const newTierData = {
            currentTier: {
              line_count: totalExistingMembers,
              price_per_line: currentTier.price_per_line
            },
            nextTier: nextTier ? {
              line_count: nextTier.line_count,
              price_per_line: nextTier.price_per_line
            } : null,
            membersToNextTier,
            circleId: circle.id,
            circleName: circle.name,
            carrier: carrier,
            carrierDisplay: data.carrier?.name || 'Mobile',
            carrierPlan: selectedPlan.name || '',
            availableSlots: circle.available_slots,
            totalJoiners: totalExistingMembers,
            features: selectedPlan.features || [],
            maxTiers: maxTiers
          };

          // Update state with the processed data if this is the selected circle
          if (!isPrefetch && circleId === selectedCircleId) {
            setTierData(newTierData);
            setTiers(formattedTiers);
          }

          // Always cache the results
          setCircleCache(prev => ({
            ...prev,
            [circleId]: {
              circleData: data,
              tierData: newTierData,
              tiers: formattedTiers,
              timestamp: Date.now()
            }
          }));

          console.log(`Cached tier data for circle ${circleId}`, {
            currentTier: newTierData.currentTier,
            nextTier: newTierData.nextTier,
            membersToNextTier
          });
        }
      }
    } catch (err) {
      console.error(`Error fetching circle details for ${circleId}:`, err);
      if (!isPrefetch) {
        setError('Failed to load circle details. Please try again later.');
      }
    } finally {
      setFetchingCircleId(null);
      if (!isPrefetch) {
        setLoadingCircleDetails(false);
      }
    }
  };

  // Determine if we're currently loading
  const isLoading = loadingCircles || loadingCircleDetails;

  /**
   * Enhanced refreshData function with multiple refresh strategies
   * @param {boolean} forceFullRefresh - Whether to force a complete refresh bypassing cache
   * @param {number} circleIdToUpdate - Optional specific circle ID to update
   * @returns {Promise<boolean>} - Success status of the refresh operation
   */
  const refreshData = async (forceFullRefresh = false, circleIdToUpdate = null) => {
    try {
      console.log(`Refreshing data: forceFullRefresh=${forceFullRefresh}, circleIdToUpdate=${circleIdToUpdate}`);
      
      // STRATEGY 1: Clear selected caches
      if (forceFullRefresh) {
        console.log("Clearing all circle cache...");
        setCircleCache({}); // Clear all circle cache
      } else if (circleIdToUpdate) {
        console.log(`Clearing cache for circle ${circleIdToUpdate}...`);
        setCircleCache(prev => {
          const newCache = { ...prev };
          delete newCache[circleIdToUpdate];
          return newCache;
        });
      } else if (selectedCircleId) {
        console.log(`Clearing cache for selected circle ${selectedCircleId}...`);
        setCircleCache(prev => {
          const newCache = { ...prev };
          delete newCache[selectedCircleId];
          return newCache;
        });
      }
      
      // STRATEGY 2: Force a direct data refresh from API
      if (forceFullRefresh || circleIdToUpdate) {
        try {
          // Get fresh auth token
          const token = await getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: 'openid profile email'
          });
          
          // DIRECT FETCH: Bypass all state and caching mechanisms
          console.log("Performing direct API fetch for fresh data...");
          const response = await circleService.getAllUserCircles(token);
          
          if (response && response.data && Array.isArray(response.data)) {
            console.log(`Received ${response.data.length} circles from API`);
            
            // Process the fresh data
            const processedCircles = response.data.map(circle => {
              const statusDisplay = getDisplayStatus(circle);
              
              // Log status changes for the specific circle we're updating
              if (circleIdToUpdate && circle.id === circleIdToUpdate) {
                console.log(`Circle ${circle.id} current status:`, {
                  status: circle.status,
                  subscription_status: circle.subscription_status,
                  displayStatus: statusDisplay
                });
              }
              
              return {
                ...circle,
                id: circle.id,
                name: circle.name || 'Circle',
                plan: circle.plan_name || 'Unlimited Plan',
                plan_id: circle.plan_id,
                carrier: circle.carrier,
                status: circle.status || 'inactive',
                subscription_status: circle.subscription_status,
                statusDisplay: statusDisplay,
                members: circle.members || [],
                ageInMonths: getAgeInMonths(circle.created_at),
                isOwner: circle.is_owner === true, // Use backend flag
                monthlyPayment: parseFloat(circle.monthly_payment_per_line || circle.amount) || 0,
                stripe_subscription_id: circle.stripe_subscription_id,
                paymentStatus: circle.status === 'active' ? 'On time' : 'Late',
                hasIssues: circle.status === 'payment_failed' || circle.status === 'past_due' || circle.status === 'canceling',
                issues: getIssuesFromCircle(circle),
                available_slots: circle.available_slots || 0,
                total_joiners: circle.total_joiners || 0,
                family_size: circle.family_size || 2,
                maxCapacity: circle.max_capacity || 10,
                features: circle.features || extractFeaturesFromPlan(circle.plan_name),
                monthly_payment_date: circle.monthly_payment_date,
                owner_id: circle.owner_id,
                source: circle.is_owner ? 'circle-owner' : 'subscription',
                // Ensure we have consistent formatting for backend fields
                joining_condition: circle.joining_condition || circle.joiningCondition || 'switching_carriers',
                shipping_method: determineShippingMethod(circle),
                // Flag this as a fresh data fetch
                _refreshed: true,
                _refreshTimestamp: Date.now()
              };
            });
            
            // Update our state with the fresh data
            setUserCircles(processedCircles);
            console.log("State updated with fresh circles data");
            
            // If we're updating a specific circle, refresh its details too
            if (circleIdToUpdate && selectedCircleId === circleIdToUpdate) {
              console.log(`Also refreshing details for circle ${circleIdToUpdate}...`);
              await fetchCircleDetails(circleIdToUpdate);
            }
          }
        } catch (apiError) {
          console.error('Error in direct data refresh:', apiError);
        }
      }
      
      // STRATEGY 3: Update the last updated timestamp to trigger useEffect hooks
      setLastUpdated(Date.now());
      console.log("Refresh complete, updated timestamp to trigger effects");
      
      return true;
    } catch (error) {
      console.error('Error in refreshData:', error);
      return false;
    }
  };

  // Value to be provided to consumers
  const contextValue = {
    // Data
    userCircles,
    circleData,
    tierData,
    tiers,

    // Circle selection
    selectedCircleId,
    setSelectedCircleId,

    // Status
    isLoading,
    error,

    // Price map for all circles
    circlePriceMap,

    // Enhanced refresh actions
    refreshData,
    forceFullRefresh: () => refreshData(true),
    refreshCircle: (circleId) => refreshData(true, circleId),
    fetchCircleDetails
  };

  return (
    <CircleDataContext.Provider value={contextValue}>
      {children}
    </CircleDataContext.Provider>
  );
};

/**
 * Custom hook to use the circle data
 * Use this in any component that needs access to circle data
 */
export const useCircleData = () => {
  const context = useContext(CircleDataContext);
  if (!context) {
    throw new Error('useCircleData must be used within a CircleDataProvider');
  }
  return context;
};